import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Typography, Spin, Progress, Button, Input } from 'antd';
import { customerPortalService, PortalData, PortalReceipt, PortalDebt } from '../../services/customerPortalService';
import ErrorPage from '../../components/customer-portal/ErrorPage';
import TransactionHistory, { TransactionItem } from '../../components/customer-portal/TransactionHistory';
import TransactionHistoryMobile from '../../components/customer-portal/TransactionHistoryMobile';
import { formatCurrency } from '../../utils/formatters';
import { useResponsive } from '../../hooks/useResponsive';
import './CustomerPortalPage.css';

const PASSWORD_STORAGE_KEY = 'customer_portal_password';

const getStoredPortalPassword = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(PASSWORD_STORAGE_KEY);
  } catch {
    return null;
  }
};

const savePortalPassword = (password: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(PASSWORD_STORAGE_KEY, password);
  } catch {
    // ignore
  }
};

const clearPortalPassword = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PASSWORD_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const CustomerPortalPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { isMobile } = useResponsive();
  const defaultTitle = 'Hoa Sinh Bio - Cổng thông tin khách hàng';
  const ogImagePath = '/logo/logo-ts.png';
  const numberFormatter = new Intl.NumberFormat('vi-VN');
  const formatNumber = (value: number) => numberFormatter.format(value || 0);
  const [activePassword, setActivePassword] = useState<string | null>(() => getStoredPortalPassword());
  const [passwordInput, setPasswordInput] = useState(() => getStoredPortalPassword() || '');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [authErrorMessage, setAuthErrorMessage] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<PortalData>(
    ['portal', token, activePassword],
    () => customerPortalService.getPortalData(token!, activePassword!),
    {
      enabled: !!token && !!activePassword,
      retry: false,
      staleTime: 5 * 60 * 1000,
      onSuccess: () => {
        if (activePassword) {
          savePortalPassword(activePassword);
        }
        setAuthErrorMessage(null);
        setPasswordError(null);
      },
      onError: (err: any) => {
        if (err?.response?.status === 401) {
          const message = err?.response?.data?.message || 'Mật khẩu không chính xác';
          setAuthErrorMessage(message);
          clearPortalPassword();
          setActivePassword(null);
        }
      },
    }
  );

  useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);

    return () => {
      document.head.removeChild(metaTag);
    };
  }, []);

  useEffect(() => {
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    const rootEl = document.getElementById('root');
    const previousRootOverflow = rootEl?.style.overflow || '';

    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    if (rootEl) {
      rootEl.style.overflow = 'auto';
    }

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
      if (rootEl) {
        rootEl.style.overflow = previousRootOverflow;
      }
    };
  }, []);

  useEffect(() => {
    const customerName = data?.customer?.name;
    const portalTitle = customerName ? `${customerName} | Hoa Sinh Bio` : defaultTitle;
    const description = customerName
      ? `Cổng thông tin khách hàng cho ${customerName}, theo dõi công nợ và giao dịch.`
      : 'Cổng thông tin khách hàng Hoa Sinh Bio, theo dõi công nợ và giao dịch.';

    document.title = portalTitle;

    const fullOgImageUrl =
      window.location.origin + ogImagePath;

    const metaConfigs = [
      { selector: 'meta[name="description"]', attributes: { name: 'description', content: description } },
      { selector: 'meta[property="og:title"]', attributes: { property: 'og:title', content: portalTitle } },
      { selector: 'meta[property="og:description"]', attributes: { property: 'og:description', content: description } },
      { selector: 'meta[property="og:type"]', attributes: { property: 'og:type', content: 'website' } },
      { selector: 'meta[property="og:image"]', attributes: { property: 'og:image', content: fullOgImageUrl } },
      { selector: 'meta[name="twitter:image"]', attributes: { name: 'twitter:image', content: fullOgImageUrl } },
    ];

    const createdNodes: HTMLElement[] = [];
    const previousStates: Array<{ node: Element; content: string | null }> = [];

    metaConfigs.forEach(({ selector, attributes }) => {
      let node = document.head.querySelector(selector);
      if (!node) {
        node = document.createElement('meta');
        Object.entries(attributes).forEach(([key, value]) => node?.setAttribute(key, value));
        document.head.appendChild(node);
        createdNodes.push(node as HTMLElement);
      } else {
        previousStates.push({ node, content: node.getAttribute('content') });
        Object.entries(attributes).forEach(([key, value]) => node?.setAttribute(key, value));
      }
    });

    return () => {
      document.title = defaultTitle;
      createdNodes.forEach((node) => document.head.removeChild(node));
      previousStates.forEach(({ node, content }) => {
        if (content !== null) {
          node.setAttribute('content', content);
        }
      });
    };
  }, [data, defaultTitle]);

  const transactions = useMemo(() => {
    if (!data) {
      return [];
    }

    const receipts = data.receipts || [];
    const debts = data.debts || [];

    const combined: TransactionItem[] = [];

    const parseDate = (value?: string | null) => {
      if (!value) return '';
      return value.replace(' ', 'T');
    };

    receipts.forEach((receipt: PortalReceipt) => {
      combined.push({
        id: `receipt-${receipt.id}`,
        type: 'receipt',
        code: receipt.voucher_number,
        date: parseDate(receipt.created_at),
        description: `${receipt.item_count} sản phẩm`,
        amount: receipt.total_amount,
        status: receipt.status,
        receiptId: receipt.id,
      });
    });

    debts.forEach((debt: PortalDebt) => {
      debt.payments?.forEach((payment) => {
        combined.push({
          id: `payment-${payment.id}`,
          type: 'payment',
          code: payment.note && payment.note.trim().length > 0
            ? payment.note
            : debt.voucher_number
              ? `Thanh toán ${debt.voucher_number}`
              : `Thanh toán CN-${debt.id}`,
          date: parseDate(payment.payment_date || payment.created_at),
          description: `Thanh toán công nợ phiếu ${debt.voucher_number || debt.id}`,
          amount: -payment.amount,
          relatedVoucherId: debt.stock_out_voucher_id || undefined,
        });
      });
    });

    const sortedAsc = [...combined].sort((a, b) => {
      const dateA = new Date(a.date || '').getTime();
      const dateB = new Date(b.date || '').getTime();
      return dateA - dateB;
    });

    let runningBalance = 0;
    const runningMap = new Map<string, number>();
    sortedAsc.forEach((item) => {
      runningBalance += item.amount;
      runningMap.set(item.id, runningBalance);
    });

    return combined
      .map((item) => ({
        ...item,
        runningBalance: runningMap.get(item.id) || 0,
      }))
      .sort((a, b) => {
        const dateA = new Date(a.date || '').getTime();
        const dateB = new Date(b.date || '').getTime();
        return dateB - dateA;
      });
  }, [data]);

  const portalError = error as any;
  const isUnauthorizedError = portalError?.response?.status === 401;

  if (portalError && !isUnauthorizedError && activePassword) {
    return <ErrorPage />;
  }

  const customer = data?.customer ?? {
    name: 'Đang tải...',
    code: '',
  };
  const summary = data?.summary ?? {
    total_debt: 0,
    paid_this_month: 0,
  };
  const loyaltySummary = data?.loyalty ?? {
    year: new Date().getFullYear(),
    percentage: data?.customer?.loyalty_percentage ?? 1,
    total_purchase_value: 0,
    receipt_count: 0,
    points_earned: 0,
  };
  const debts = data?.debts ?? [];
  const unpaidTotal = debts.reduce((sum, debt) => sum + (debt.debt_amount - debt.paid_amount), 0);
  const paymentTotal = summary.paid_this_month + unpaidTotal;
  const paymentProgressValue = paymentTotal > 0 ? Math.round((summary.paid_this_month / paymentTotal) * 100) : 0;
  const isPasswordGateVisible = !activePassword;
  const passwordFeedback = passwordError || authErrorMessage;

  type StatItem = {
    title: string;
    highlight: string;
    breakdown?: { label: string; value: string }[];
    extraNote?: string;
    qrImage?: string;
    twoColumn?: boolean;
  };

  const statItems: StatItem[] = [
    {
      title: 'Tổng công nợ hiện tại',
      highlight: formatCurrency(summary.total_debt),
      breakdown: [
        { label: 'Còn phải trả', value: formatCurrency(unpaidTotal) },
        { label: 'Đã thanh toán', value: formatCurrency(summary.paid_this_month) }
      ]
    },
    {
      title: 'Thông tin thanh toán',
      highlight: 'Vietcombank',
      breakdown: [
        { label: 'Số TK', value: '7966775195' },
        { label: 'Chủ TK', value: 'PHẠM ĐÌNH CHUNG' }
      ],
      extraNote: 'Nội dung: KH + mã chứng từ (VD: KH001 11)',
      qrImage: '/image/vietqr-7966775195.png',
      twoColumn: true
    },
    {
      title: `Điểm tích lũy ${loyaltySummary.year}`,
      highlight: `${formatNumber(loyaltySummary.points_earned)} điểm`,
      breakdown: [
        { label: 'Giá trị mua', value: formatCurrency(loyaltySummary.total_purchase_value || 0) },
        { label: 'Tỷ lệ ưu đãi', value: `${(loyaltySummary.percentage ?? 0).toFixed(1)}%` },
        { label: 'Số phiếu', value: `${loyaltySummary.receipt_count || 0}` }
      ],
      extraNote: 'Điểm sẽ được tính lại mỗi năm.'
    }
  ];

  const handlePasswordSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = passwordInput.trim();
    if (!trimmed) {
      setPasswordError('Vui lòng nhập mật khẩu');
      return;
    }
    setPasswordError(null);
    setAuthErrorMessage(null);
    setActivePassword(trimmed);
  };

  return (
    <div className="customer-portal">
      <header className="portal-header">
        <div className="portal-header__inner">
          <div className="portal-header__brand">
            <img src="/logo/logo.png" alt="Hoa Sinh Bio" className="portal-logo" />
            <div className="portal-header__brand-text" style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em' }}>CÔNG TY TNHH HOA SINH BIO</div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>CLIENT PORTAL</div>
            </div>
          </div>
          <div className="portal-header__customer">
            <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <div className="portal-header__customer-name">{customer.name}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{customer.code || '—'}</div>
            </div>
          </div>
        </div>
      </header>

      {isPasswordGateVisible ? (
        <main className="portal-main">
          <div className="portal-tab">
            <section className="portal-password-card">
              <Typography.Title level={4} style={{ marginBottom: 8 }}>
                Nhập mật khẩu truy cập
              </Typography.Title>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Vui lòng liên hệ Hoa Sinh Bio nếu bạn chưa nhận được mật khẩu.
              </Typography.Paragraph>

              <form onSubmit={handlePasswordSubmit}>
                <Input.Password
                  size="large"
                  placeholder="Nhập mật khẩu"
                  value={passwordInput}
                  status={passwordFeedback ? 'error' : undefined}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  autoFocus
                />
                {passwordFeedback && (
                  <Typography.Text className="portal-password-card__error">
                    {passwordFeedback}
                  </Typography.Text>
                )}
                <Button type="primary" htmlType="submit" size="large" block>
                  Xem thông tin
                </Button>
              </form>
            </section>
          </div>
        </main>
      ) : (
        <Spin spinning={!!activePassword && isLoading} tip="Đang tải dữ liệu..." size="large">
          <main className="portal-main">
            <div className="portal-tab">
              <section className="portal-card-grid">
                {statItems.map((item) => {
                  const isBankCard = item.title === 'Thông tin thanh toán';
                  const twoColumnLayout = item.twoColumn && !isMobile;
                  const qrMaxWidth = isMobile ? 200 : 200;

                  return (
                    <div className="portal-stat-card" key={item.title}>
                      {item.twoColumn ? (
                        <div
                          style={{
                            display: 'grid',
                            gap: isMobile ? 12 : 16,
                            gridTemplateColumns: twoColumnLayout ? 'minmax(220px, 1fr) minmax(160px, 0.7fr)' : '1fr',
                            alignItems: 'start'
                          }}
                        >
                          <div>
                            <Typography.Text type="secondary">{item.title}</Typography.Text>
                            <div className="portal-stat-card__value" style={{ marginTop: 6 }}>
                              {item.highlight}
                            </div>
                            <div style={{ display: 'grid', gap: 8 }}>
                              {item.breakdown?.map((row) => {
                                const shrinkBankInfo = isBankCard && (row.label === 'Số TK' || row.label === 'Chủ TK');

                                return (
                                  <div
                                    key={row.label}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      fontSize: shrinkBankInfo ? (isMobile ? 14 : 16) : (isMobile ? 16 : 21)
                                    }}
                                  >
                                    <span style={{ color: '#94a3b8' }}>{row.label}</span>
                                    <span style={{ fontWeight: 600, fontSize: shrinkBankInfo ? (isMobile ? 13 : 14) : undefined }}>
                                      {row.value}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            {item.extraNote && (
                              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
                                {item.extraNote}
                              </Typography.Text>
                            )}
                          </div>
                          {item.qrImage && (
                            <div
                              style={{
                                textAlign: 'center',
                                justifySelf: twoColumnLayout ? 'stretch' : 'center',
                                marginTop: twoColumnLayout ? 0 : 12
                              }}
                            >
                              <img
                                src={item.qrImage}
                                alt="VietQR chuyển khoản Hoa Sinh Bio"
                                style={{
                                  maxWidth: qrMaxWidth,
                                  width: twoColumnLayout ? '100%' : '85%',
                                  minWidth: twoColumnLayout ? undefined : 140,
                                  borderRadius: 8,
                                  boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)'
                                }}
                                loading="lazy"
                              />
                              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                                Quét mã để chuyển khoản nhanh
                              </Typography.Text>
                              <div style={{ marginTop: 8 }}>
                                <a
                                  href={item.qrImage}
                                  download="vietqr-hoa-sinh-bio.png"
                                  style={{
                                    display: 'inline-block',
                                    padding: '6px 12px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    borderRadius: 8,
                                    border: '1px solid #cbd5f5',
                                    color: '#0f172a',
                                    textDecoration: 'none'
                                  }}
                                >
                                  Tải QR Code
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <Typography.Text type="secondary">{item.title}</Typography.Text>
                          <div className="portal-stat-card__value">{item.highlight}</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {item.breakdown?.map((row) => {
                            const shrinkBankInfo = isBankCard && (row.label === 'Số TK' || row.label === 'Chủ TK');

                            return (
                              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#94a3b8' }}>{row.label}</span>
                                <span style={{ fontWeight: 600, fontSize: shrinkBankInfo ? 13 : undefined }}>
                                  {row.value}
                                </span>
                              </div>
                            );
                          })}
                          </div>
                        {item.title === 'Tổng công nợ hiện tại' && (
                          <div className="portal-summary-progress">
                            <div className="portal-summary-progress__header">
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                Đã thanh toán {paymentProgressValue}%
                              </Typography.Text>
                              <Typography.Text strong style={{ fontSize: 12 }}>
                                {formatCurrency(summary.paid_this_month)}
                              </Typography.Text>
                            </div>
                            <Progress
                              percent={paymentProgressValue}
                              showInfo={false}
                              size={{ height: 8 }}
                              strokeColor="#16a34a"
                              trailColor="#e2e8f0"
                            />
                          </div>
                        )}
                          {item.extraNote && (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {item.extraNote}
                            </Typography.Text>
                          )}
                          {item.qrImage && (
                            <div style={{ marginTop: 12, textAlign: 'center' }}>
                              <img
                                src={item.qrImage}
                                alt="VietQR chuyển khoản Hoa Sinh Bio"
                                style={{ maxWidth: 160, width: '100%', borderRadius: 8 }}
                                loading="lazy"
                              />
                              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                                Quét mã để chuyển khoản nhanh
                              </Typography.Text>
                              <div style={{ marginTop: 8 }}>
                                <a
                                  href={item.qrImage}
                                  download="vietqr-hoa-sinh-bio.png"
                                  style={{
                                    display: 'inline-block',
                                    padding: '6px 12px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    borderRadius: 8,
                                    border: '1px solid #cbd5f5',
                                    color: '#0f172a',
                                    textDecoration: 'none'
                                  }}
                                >
                                  Tải QR
                                </a>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </section>

              <section className="portal-table-card">
                <div className="portal-table-card__header">
                  <Typography.Title level={4} style={{ margin: 0 }}>Lịch sử giao dịch</Typography.Title>
                  {isMobile && (
                    <Typography.Text type="secondary" style={{ fontSize: 15 }}>
                      {transactions.length} giao dịch
                    </Typography.Text>
                  )}
                </div>
                <div className="portal-table-card__body">
                  {isMobile ? (
                    <TransactionHistoryMobile token={token!} password={activePassword!} transactions={transactions} />
                  ) : (
                    <TransactionHistory token={token!} password={activePassword!} transactions={transactions} />
                  )}
                </div>
              </section>
            </div>
          </main>
        </Spin>
      )}

      <footer className="portal-footer">
        © {new Date().getFullYear()} Hoa Sinh Bio Customer Portal. All rights reserved.
      </footer>
    </div>
  );
};

export default CustomerPortalPage;
